import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal, Eyebrow } from "./MarketingUI";
import { Search, Radar, UserCheck, FileText, Bell, BarChart3 } from "lucide-react";

const stages = [
  { icon: Search, title: "Discover", desc: "Surface bills across sources and sessions." },
  { icon: Radar, title: "Track", desc: "Follow every legislative action and change." },
  { icon: UserCheck, title: "Assign", desc: "Route legislation to the right staff." },
  { icon: FileText, title: "Analyze", desc: "Capture institutional analysis and notes." },
  { icon: Bell, title: "Monitor", desc: "Stay ahead with watchlists and alerts." },
  { icon: BarChart3, title: "Report", desc: "Turn activity into leadership-ready output." },
];

export default function Workflow() {
  const reduce = useReducedMotion();
  return (
    <section id="capabilities" className="border-y border-brand-line bg-white py-24 sm:py-28" data-testid="workflow-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>One legislative command center</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-ink sm:text-4xl">
              From introduced bill to internal action.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 text-base leading-relaxed text-brand-slate">
              GovFlowState is more than a bill-search tool. It carries legislation through the full arc of government work.
            </p>
          </Reveal>
        </div>

        <div className="relative mt-16">
          {/* connecting line */}
          <div className="absolute left-0 right-0 top-7 hidden h-px bg-brand-line lg:block">
            <motion.div
              className="h-full origin-left bg-gradient-to-r from-brand-civic via-brand-teal to-brand-gold"
              initial={reduce ? false : { scaleX: 0 }}
              whileInView={reduce ? {} : { scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: "easeInOut" }}
            />
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-6">
            {stages.map((s, i) => (
              <Reveal key={s.title} delay={0.1 * i}>
                <div className="relative text-center lg:text-left" data-testid={`workflow-stage-${i}`}>
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-brand-line bg-white shadow-card lg:mx-0">
                    <s.icon className="h-6 w-6 text-brand-navy" strokeWidth={1.8} />
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-1.5 lg:justify-start">
                    <span className="font-mono text-[11px] font-bold text-brand-gold">0{i + 1}</span>
                    <h3 className="font-display text-base font-bold text-brand-ink">{s.title}</h3>
                  </div>
                  <p className="mt-1.5 text-sm leading-snug text-brand-slate">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
